import { z } from 'zod';
import { cursorQuerySchema, urlHttpHoacNoiBoSchema } from './core.js';
import { maKySchema } from './ky.js';
/* ------------------------------------------------------------------ */
/* Hằng số                                                             */
/* ------------------------------------------------------------------ */
export const PARTNER_KINDS = ['mcn', 'adsense_direct'];
export const partnerKindSchema = z.enum(PARTNER_KINDS);
export const PARTNER_KIND_LABEL = {
    mcn: 'Network (MCN)',
    adsense_direct: 'AdSense trực tiếp',
};
/** Đối tác `adsense_direct` = một tài khoản AdSense của công ty. */
export function laAdsense(kind) {
    return kind === 'adsense_direct';
}
/**
 * Nhãn cột "Network" của báo cáo: tài khoản AdSense ghi rõ là AdSense để không
 * ai đọc nhầm thành một MCN.
 */
export const TIEN_TO_ADSENSE = 'AdSense · ';
export function nhanDoiTacTraTien(kind, tenDoiTac) {
    if (!tenDoiTac)
        return null;
    if (kind !== 'adsense_direct')
        return tenDoiTac;
    // Gắn tiền tố một lần: nhãn còn chảy qua `layTheoDoiTac` (gộp theo đối tác).
    return tenDoiTac.startsWith(TIEN_TO_ADSENSE) ? tenDoiTac : `${TIEN_TO_ADSENSE}${tenDoiTac}`;
}
/** Thuế Google khấu trừ mặc định trên phần doanh thu phát sinh ở Mỹ. */
export const THUE_MY_MAC_DINH_PCT = 30;
/** Ngưỡng chi trả mặc định của một tài khoản AdSense. */
export const NGUONG_CHI_TRA_ADSENSE = 100;
/** Mã nhà xuất bản AdSense: `pub-` + 16 chữ số. */
export const publisherCodeSchema = z
    .string()
    .trim()
    .regex(/^pub-\d{16}$/, 'Mã nhà xuất bản phải dạng pub- kèm 16 chữ số');
export const PARTNER_CATEGORIES = ['general', 'music'];
export const partnerCategorySchema = z.enum(PARTNER_CATEGORIES);
export const CONTRACT_STATUSES = ['active', 'expired', 'pending'];
export const contractStatusSchema = z.enum(CONTRACT_STATUSES);
export const ADSENSE_LINK_STATUSES = ['pending', 'linked'];
export const adsenseLinkStatusSchema = z.enum(ADSENSE_LINK_STATUSES);
export const STATEMENT_STATUSES = ['pending', 'reconciled', 'confirmed', 'held', 'paid'];
export const statementStatusSchema = z.enum(STATEMENT_STATUSES);
export const STATEMENT_STATUS_LABEL = {
    pending: 'Chờ đối soát',
    reconciled: 'Đã đối soát',
    confirmed: 'Đã xác nhận',
    held: 'Đang giữ lại',
    paid: 'Đã thanh toán',
};
export const TICKET_STATUSES = ['open', 'in_progress', 'done'];
export const ticketStatusSchema = z.enum(TICKET_STATUSES);
/** Kỳ dạng `YYYY-MM`. */
export const periodMonthSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Kỳ phải dạng YYYY-MM');
/* ------------------------------------------------------------------ */
/* Đối tác và hợp đồng                                                 */
/* ------------------------------------------------------------------ */
export const partnerSchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: partnerKindSchema,
    category: partnerCategorySchema,
    logoColor: z.string(),
    contactName: z.string().nullable(),
    contactEmail: z.string().nullable(),
    notes: z.string().nullable(),
    /// Mã nhà xuất bản AdSense (`pub-…`); `null` với đối tác MCN.
    publisherCode: z.string().nullable(),
    /// Số kênh đang gán cho đối tác này.
    channelCount: z.number().int().nonnegative(),
    /// Tỷ lệ Network giữ lại theo hợp đồng đang hiệu lực.
    defaultSharePercent: z.string().nullable(),
    /// Thuế Mỹ khấu trừ theo hợp đồng đang hiệu lực.
    thueMyPct: z.string().nullable(),
    contractStatus: contractStatusSchema.nullable(),
});
export const partnerContractSchema = z.object({
    id: z.string(),
    partnerId: z.string(),
    contractNo: z.string(),
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime().nullable(),
    defaultSharePercent: z.string(),
    /// Thuế Google khấu trừ trên phần doanh thu ở Mỹ. Nguồn duy nhất của thuế Mỹ.
    thueMyPct: z.string(),
    paymentTerms: z.string().nullable(),
    paymentThreshold: z.string().nullable(),
    currency: z.string(),
    fileUrl: z.string().nullable(),
    status: contractStatusSchema,
});
export const channelPartnerLinkSchema = z.object({
    id: z.string(),
    channelId: z.string(),
    partnerId: z.string(),
    partnerName: z.string(),
    partnerKind: partnerKindSchema,
    contractId: z.string().nullable(),
    sharePercent: z.string().nullable(),
    joinedAt: z.iso.datetime(),
    leftAt: z.iso.datetime().nullable(),
    linkStatus: adsenseLinkStatusSchema.nullable(),
});
/* ------------------------------------------------------------------ */
/* Sao kê                                                              */
/* ------------------------------------------------------------------ */
export const statementLineSchema = z.object({
    id: z.string(),
    statementId: z.string(),
    channelId: z.string().nullable(),
    channelName: z.string().nullable(),
    rawChannelRef: z.string(),
    gross: z.string(),
    partnerShare: z.string(),
    net: z.string(),
});
export const statementSchema = z.object({
    id: z.string(),
    partnerId: z.string(),
    partnerName: z.string(),
    period: z.string(),
    currency: z.string(),
    grossTotal: z.string(),
    partnerShareTotal: z.string(),
    netTotal: z.string(),
    status: statementStatusSchema,
    paidAt: z.iso.datetime().nullable(),
    sourceFile: z.string().nullable(),
    lineCount: z.number().int().nonnegative(),
    /// Số dòng chưa khớp được với kênh nào.
    unmatchedCount: z.number().int().nonnegative(),
});
export const statementTotalsSchema = z.object({
    period: z.string(),
    currency: z.string(),
    grossTotal: z.string(),
    partnerShareTotal: z.string(),
    netTotal: z.string(),
    statementCount: z.number().int().nonnegative(),
});
/** Kết quả `computeExpectedSplit`: phần Network giữ và phần công ty nhận. */
export const expectedSplitSchema = z.object({
    channelId: z.string(),
    period: z.string(),
    gross: z.string(),
    sharePercent: z.string(),
    partnerShare: z.string(),
    net: z.string(),
    currency: z.string(),
    partnerId: z.string().nullable(),
});
/* ------------------------------------------------------------------ */
/* Ticket                                                              */
/* ------------------------------------------------------------------ */
export const partnerTicketSchema = z.object({
    id: z.string(),
    partnerId: z.string(),
    partnerName: z.string(),
    channelId: z.string().nullable(),
    subject: z.string(),
    body: z.string(),
    status: ticketStatusSchema,
    createdAt: z.iso.datetime(),
});
/* ------------------------------------------------------------------ */
/* Input                                                               */
/* ------------------------------------------------------------------ */
export const createPartnerInputSchema = z.object({
    name: z.string().trim().min(2).max(120),
    kind: partnerKindSchema.default('mcn'),
    category: partnerCategorySchema.default('general'),
    logoColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional(),
    contactName: z.string().trim().max(120).nullish(),
    contactEmail: z.email().nullish(),
    notes: z.string().trim().max(2000).nullish(),
    /// Chỉ cho đối tác `adsense_direct`; API từ chối nếu đặt cho MCN.
    publisherCode: publisherCodeSchema.nullish(),
    /**
     * `true` khi người dùng đã thấy cảnh báo "tên này trùng đối tác đang có" và
     * vẫn chọn tạo thêm một đối tác nữa. Thiếu cờ này thì API trả **409** kèm tên
     * đối tác đang có — xem `chuanHoaTenDoiTac`.
     */
    xacNhanTrungTen: z.boolean().optional(),
});
export const updatePartnerInputSchema = createPartnerInputSchema.partial();
export const contractInputSchema = z.object({
    /**
     * Bỏ trống với tài khoản AdSense: không có "số hợp đồng" nào để nhập, API tự
     * sinh theo ngày hiệu lực (`ADS-YYYY-MM-DD`).
     */
    contractNo: z.string().trim().min(1).max(60).optional(),
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime().nullish(),
    /** Bỏ trống với tài khoản AdSense: API ép 0. */
    defaultSharePercent: z.number().min(0).max(100).optional(),
    /** Thuế Mỹ khấu trừ; mặc định 30, đặt 0 được. */
    thueMyPct: z.number().min(0).max(100).default(THUE_MY_MAC_DINH_PCT),
    paymentTerms: z.string().trim().max(500).nullish(),
    paymentThreshold: z.number().min(0).nullish(),
    currency: z.string().length(3).default('USD'),
    /** `http(s)` hoặc đường dẫn nội bộ — xem `urlHttpHoacNoiBoSchema` (mục T1). */
    fileUrl: urlHttpHoacNoiBoSchema.nullish(),
    status: contractStatusSchema.default('active'),
    /**
     * `true` khi người dùng đã thấy cảnh báo "số này thuộc hợp đồng đã xoá" và
     * chọn **tạo hợp đồng mới** thay vì khôi phục bản cũ.
     */
    boQuaSoDaXoa: z.boolean().optional(),
});
/**
 * Sửa một hợp đồng. Trường nào không truyền thì giữ nguyên.
 *
 * `defaultSharePercent`, `thueMyPct` và `startDate` chỉ sửa đè được khi hợp đồng
 * **chưa dùng** cho kỳ đã chốt / sao kê đã xử lý; nếu đã dùng thì phải tạo phiên
 * bản mới (`taoPhienBanHopDongInputSchema`).
 */
export const capNhatHopDongInputSchema = z.object({
    contractNo: z.string().trim().min(1).max(60).optional(),
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().nullish(),
    defaultSharePercent: z.number().min(0).max(100).optional(),
    /**
     * Thuế Mỹ khấu trừ. Đổi số này khi hợp đồng **đã chia tiền** bị chặn y như
     * `defaultSharePercent`: phải tạo phiên bản mới từ một ngày.
     */
    thueMyPct: z.number().min(0).max(100).optional(),
    paymentTerms: z.string().trim().max(500).nullish(),
    paymentThreshold: z.number().min(0).nullish(),
    /** `http(s)` hoặc đường dẫn nội bộ — xem `urlHttpHoacNoiBoSchema` (mục T1). */
    fileUrl: urlHttpHoacNoiBoSchema.nullish(),
    status: contractStatusSchema.optional(),
});
/**
 * Tạo **phiên bản mới** của hợp đồng từ một ngày: đóng bản cũ vào ngày hôm
 * trước rồi tạo bản mới với tỷ lệ mới.
 *
 * Dùng khi hợp đồng đã chia tiền cho kỳ đã chốt — sửa đè tỷ lệ sẽ làm số báo
 * cáo cũ đổi theo.
 */
export const taoPhienBanHopDongInputSchema = z.object({
    tuNgay: z.iso.datetime(),
    /** Bỏ trống thì giữ tỷ lệ của bản cũ (tài khoản AdSense luôn 0). */
    defaultSharePercent: z.number().min(0).max(100).optional(),
    /** Bỏ trống thì giữ thuế Mỹ của bản cũ. */
    thueMyPct: z.number().min(0).max(100).optional(),
    /** Bỏ trống thì tự thêm hậu tố `-v2`, `-v3`… vào số hợp đồng cũ. */
    contractNo: z.string().trim().min(1).max(60).optional(),
    endDate: z.iso.datetime().nullish(),
    paymentTerms: z.string().trim().max(500).nullish(),
    paymentThreshold: z.number().min(0).nullish(),
    /** `http(s)` hoặc đường dẫn nội bộ — xem `urlHttpHoacNoiBoSchema` (mục T1). */
    fileUrl: urlHttpHoacNoiBoSchema.nullish(),
});
/**
 * Mức xoá được của một hợp đồng:
 *
 * - `ngay` — chưa gắn kênh, chưa có sao kê, chưa kỳ chốt: xoá luôn.
 * - `can-chuyen-kenh` — đã gắn kênh nhưng chưa sao kê và chưa kỳ chốt: xoá
 *   được, nhưng phải chọn cho **từng kênh** đi đâu.
 * - `chan` — có sao kê đã xử lý hoặc kỳ đã chốt: không xoá, chỉ kết thúc hợp
 *   đồng hoặc mở lại kỳ.
 */
export const MUC_DO_XOA_HOP_DONG = ['ngay', 'can-chuyen-kenh', 'chan'];
export const mucDoXoaHopDongSchema = z.enum(MUC_DO_XOA_HOP_DONG);
/** Số ngày còn khôi phục được một hợp đồng đã xoá mềm. */
export const NGAY_GIU_HOP_DONG_DA_XOA = 30;
/** Một kênh đang gắn hợp đồng, kèm thứ cần biết để chọn cách xử lý. */
export const kenhGanHopDongSchema = z.object({
    linkId: z.string(),
    channelId: z.string(),
    channelName: z.string(),
    joinedAt: z.iso.datetime(),
    leftAt: z.iso.datetime().nullable(),
    /** `null` khi link chưa có tỷ lệ riêng — chọn "giữ link" thì phải nhập %. */
    sharePercent: z.string().nullable(),
    /** Tỷ lệ đang áp dụng hôm nay (lấy từ link hoặc từ hợp đồng). */
    tyLeHienTai: z.string(),
});
/** Kỳ doanh thu đã chốt có liên quan — dùng để giải thích vì sao không xoá. */
export const kyChotLienQuanSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
    period: z.string(),
    status: z.string(),
});
/** Sao kê không còn `pending` của đối tác — cũng là lý do không xoá. */
export const saoKeLienQuanSchema = z.object({
    id: z.string(),
    period: z.string(),
    status: statementStatusSchema,
});
/** Hợp đồng có đang được dùng để chia tiền hay không, kèm lý do. */
export const tinhHinhHopDongSchema = z.object({
    contractId: z.string(),
    /** `true` khi đã có kỳ chốt / sao kê đã xử lý → không sửa đè tỷ lệ. */
    daDungChiaTien: z.boolean(),
    soKyDaChot: z.number().int().nonnegative(),
    soSaoKeDaXuLy: z.number().int().nonnegative(),
    soKenhDangGan: z.number().int().nonnegative(),
    /** `true` khi xoá được **ngay**, không phải chọn gì (mức `ngay`). */
    xoaDuoc: z.boolean(),
    lyDoKhongXoaDuoc: z.array(z.string()),
    mucDoXoa: mucDoXoaHopDongSchema,
    /** Kênh phải chọn cách xử lý trước khi xoá (mức `can-chuyen-kenh`). */
    kenhCanChon: z.array(kenhGanHopDongSchema),
    /** Hợp đồng khác của cùng đối tác, để chuyển kênh sang. */
    hopDongChuyenDuoc: z.array(partnerContractSchema),
    kyLienQuan: z.array(kyChotLienQuanSchema),
    saoKeLienQuan: z.array(saoKeLienQuanSchema),
});
/* ------------------------------------------------------------------ */
/* Xoá mềm hợp đồng                                                    */
/* ------------------------------------------------------------------ */
export const CACH_XU_LY_KENH_KHI_XOA = ['chuyen-hop-dong', 'ty-le-rieng'];
export const cachXuLyKenhKhiXoaSchema = z.enum(CACH_XU_LY_KENH_KHI_XOA);
/**
 * Chọn cho **một link kênh** đi đâu khi hợp đồng bị xoá:
 *
 * - `chuyen-hop-dong` — trỏ link sang `contractIdMoi` của cùng đối tác;
 * - `ty-le-rieng` — bỏ hợp đồng khỏi link, dùng "Tỷ lệ riêng" của link. Bắt
 *   nhập `sharePercent` nếu link chưa có.
 */
export const chonKenhKhiXoaHopDongSchema = z
    .object({
    linkId: z.string().min(1),
    cach: cachXuLyKenhKhiXoaSchema,
    contractIdMoi: z.string().min(1).nullish(),
    sharePercent: z.number().min(0).max(100).nullish(),
})
    .superRefine((v, ctx) => {
    if (v.cach === 'chuyen-hop-dong' &&
        (v.contractIdMoi === null || v.contractIdMoi === undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['contractIdMoi'],
            message: 'Chọn hợp đồng để chuyển kênh sang',
        });
    }
});
/** Thân request xoá hợp đồng. `lyDo` bắt buộc, ghi vào nhật ký. */
export const xoaHopDongInputSchema = z.object({
    lyDo: z.string().trim().min(3, 'Nhập lý do xoá (ít nhất 3 ký tự)').max(500),
    kenh: z.array(chonKenhKhiXoaHopDongSchema).default([]),
});
/**
 * Khôi phục hợp đồng đã xoá mềm.
 *
 * `ganLaiKenh` gắn lại những link đã bị **tách** lúc xoá (chọn "giữ link, dùng
 * tỷ lệ riêng"): khôi phục hợp đồng mà không gắn lại thì tab Hợp đồng đếm 0
 * kênh và luật chặn sửa/xoá không còn hiệu lực — đúng chỗ sai gặp 11/10/2026.
 */
export const khoiPhucHopDongInputSchema = z.object({
    ganLaiKenh: z.boolean().default(false),
});
/** Kết quả khôi phục: kèm số link đã gắn lại để giao diện báo lại. */
export const khoiPhucHopDongKetQuaSchema = z.object({
    hopDong: partnerContractSchema,
    soKenhDaGanLai: z.number().int().nonnegative(),
});
/** Thân request xem trước: chỉ cần các lựa chọn, không cần lý do. */
export const xemTruocXoaHopDongInputSchema = z.object({
    kenh: z.array(chonKenhKhiXoaHopDongSchema).default([]),
});
/** Tỷ lệ Network của một kênh trong một kỳ, trước và sau khi xoá. */
export const dongXemTruocXoaSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
    period: z.string(),
    truoc: z.string(),
    sau: z.string(),
    /**
     * `false` khi **không ngày nào** trong kỳ thuộc một Network — kênh chưa vào
     * Network lúc đó. Phải phân biệt với "tỷ lệ bằng 0 %": `tyLe` của kỳ không có
     * Network cũng là `0.00`, hiển thị thẳng con số đó thì đọc như một tỷ lệ đã
     * tính.
     */
    coNetworkTruoc: z.boolean(),
    coNetworkSau: z.boolean(),
    /** `true` khi sau khi xoá có ngày trong kỳ không thuộc Network nào. */
    thieuNetworkSau: z.boolean(),
});
export const xemTruocXoaHopDongSchema = z.object({
    /** Các kỳ được xem trước, dạng `YYYY-MM`, cũ trước. */
    ky: z.array(z.string()),
    /** Kỳ đang diễn ra (tháng hiện tại), để giao diện ghi chú "còn chạy". */
    kyDangDienRa: z.string(),
    dong: z.array(dongXemTruocXoaSchema),
});
/** Một hợp đồng đã xoá mềm, kèm ai xoá và còn khôi phục được đến bao giờ. */
export const hopDongDaXoaSchema = partnerContractSchema.extend({
    deletedAt: z.iso.datetime(),
    nguoiXoa: z.string().nullable(),
    lyDoXoa: z.string().nullable(),
    /** Hết ngày này thì không khôi phục được nữa. */
    khoiPhucDenNgay: z.iso.datetime(),
    conKhoiPhucDuoc: z.boolean(),
    /**
     * Số link của đối tác đang **không gắn hợp đồng nào** mà ngày vào nằm trong
     * khoảng hợp đồng này — tức các kênh đã bị tách ra lúc xoá. Khôi phục xong
     * hỏi người dùng có gắn lại hay không.
     */
    soKenhGanLaiDuoc: z.number().int().nonnegative(),
});
/**
 * Mã riêng khi lưu hợp đồng trùng số với một bản **đã xoá mềm** còn trong hạn
 * khôi phục. `error.code` của API vẫn là `CONFLICT` (danh sách mã HTTP là cố
 * định), mã này nằm ở `error.details.ma` — giao diện dựa vào đó để hỏi "Khôi
 * phục bản đã xoá" hay "Tạo hợp đồng mới".
 */
export const MA_LOI_SO_HOP_DONG_DA_XOA = 'SO_HOP_DONG_DA_XOA';
/** Chi tiết kèm mã trên, để giao diện mở được hộp thoại chọn. */
export const soHopDongDaXoaChiTietSchema = z.object({
    ma: z.literal(MA_LOI_SO_HOP_DONG_DA_XOA),
    contractId: z.string(),
    contractNo: z.string(),
    deletedAt: z.iso.datetime(),
    khoiPhucDenNgay: z.iso.datetime(),
    conKhoiPhucDuoc: z.boolean(),
});
/** `error.details` có đúng dạng của mã trên hay không. */
export function laLoiSoHopDongDaXoa(details) {
    return soHopDongDaXoaChiTietSchema.safeParse(details).success;
}
/** Một dòng nhật ký thay đổi hợp đồng. */
export const nhatKyHopDongSchema = z.object({
    id: z.string(),
    action: z.string(),
    nguoiSua: z.string().nullable(),
    luc: z.iso.datetime(),
    /** Giá trị cũ → mới, chỉ gồm trường đã đổi. */
    thayDoi: z.array(z.object({ truong: z.string(), cu: z.string(), moi: z.string() })),
});
export const assignChannelInputSchema = z.object({
    channelId: z.string().min(1),
    partnerId: z.string().min(1),
    contractId: z.string().nullish(),
    sharePercent: z.number().min(0).max(100).nullish(),
    joinedAt: z.iso.datetime().optional(),
    linkStatus: adsenseLinkStatusSchema.nullish(),
});
/**
 * Sửa một dòng link: ngày hiệu lực và tỷ lệ.
 *
 * `leftAt = null` nghĩa là "đang hiệu lực"; để sửa về đang hiệu lực thì truyền
 * `null`, còn **không truyền** thì giữ nguyên.
 */
export const updateChannelLinkInputSchema = z.object({
    joinedAt: z.iso.datetime().optional(),
    leftAt: z.iso.datetime().nullish(),
    sharePercent: z.number().min(0).max(100).nullish(),
    /**
     * Gắn dòng link vào một hợp đồng của **cùng đối tác**, hoặc `null` để tách ra.
     * Không truyền thì giữ nguyên.
     */
    contractId: z.string().min(1).nullish(),
});
/**
 * Dòng link **rác**: vào và rời trong **cùng một ngày**.
 *
 * Giao diện và API phải dùng **cùng một luật**, nếu không sẽ như lỗi đã gặp: nút
 * "Xoá dòng rác" hiện (web so theo ngày) mà API từ chối (so theo timestamp).
 * Nhận khoá ngày `YYYY-MM-DD` hoặc ISO datetime; cắt 10 ký tự đầu là ngày.
 */
export function laLinkRacTheoNgay(link) {
    return link.leftAt !== null && link.joinedAt.slice(0, 10) === link.leftAt.slice(0, 10);
}
/**
 * Hợp đồng của đối tác **phủ một ngày lịch** `YYYY-MM-DD`, bản hiệu lực muộn
 * nhất trước.
 *
 * Giao diện và API phải dùng **cùng một luật** chọn hợp đồng, nếu không sẽ như
 * lỗi đã gặp: form "Gán kênh" lọc theo chữ `status === 'active'` (chữ đó lạc
 * hậu được) nên không thấy hợp đồng BerlinTomek còn `pending`, trong khi API
 * chọn theo khoảng ngày. Bản API là `hopDongPhuNgayVao` trong module networks;
 * hàm này là bản cho DTO (ngày dạng ISO).
 *
 * Danh sách DTO từ `GET /:id/hop-dong` đã bỏ bản xoá mềm, nên ở đây không cần
 * kiểm `deletedAt`.
 */
export function hopDongPhuNgay(hopDong, ngay) {
    return hopDong
        .filter((c) => c.startDate.slice(0, 10) <= ngay && (c.endDate === null || ngay <= c.endDate.slice(0, 10)))
        .sort((a, b) => b.startDate.localeCompare(a.startDate));
}
/* ------------------------------------------------------------------ */
/* Danh bạ đối tác — chống tạo trùng                                   */
/* ------------------------------------------------------------------ */
/**
 * Một dòng **danh bạ đối tác**: chỉ id, tên và loại.
 *
 * Người bị giới hạn theo kênh được giao chỉ thấy đối tác nào đã gắn với kênh
 * của họ (`doiTacDuocXem`). Hệ quả thật trên bản chạy thật: họ không biết công
 * ty đã khai "RouteNote" nên khai lại một "Route Note" nữa, và từ đó mỗi bên
 * một hợp đồng, một tỷ lệ chia, một mức thuế Mỹ.
 *
 * Danh bạ mở đúng **ba trường** cho mọi người có `networks:edit`: đủ để chọn
 * đúng đối tác, không đủ để biết đối tác đó đang trả bao nhiêu tiền cho kênh
 * nào. Không số liệu, không danh sách kênh, không sao kê.
 */
export const doiTacDanhBaSchema = z.object({
    id: z.string(),
    name: z.string(),
    kind: partnerKindSchema,
});
/**
 * Điều khoản **chỉ đọc** của một hợp đồng trong danh bạ: vừa đủ để người gán
 * kênh biết mình đang gắn kênh vào cái gì (tỷ lệ chia, thuế Mỹ, khoảng ngày).
 *
 * Cố ý **không** có `paymentTerms`, `paymentThreshold`, `fileUrl`: đó là điều
 * khoản thương mại và file hợp đồng của đối tác, không liên quan tới việc chọn
 * đúng bản cho một kênh.
 */
export const hopDongDanhBaSchema = z.object({
    id: z.string(),
    partnerId: z.string(),
    contractNo: z.string(),
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime().nullable(),
    defaultSharePercent: z.string(),
    thueMyPct: z.string(),
    currency: z.string(),
    status: contractStatusSchema,
});
/**
 * Khoá so trùng tên đối tác: bỏ dấu tiếng Việt, hạ chữ thường, bỏ **mọi** ký
 * tự không phải chữ hay số.
 *
 * Nhờ vậy `RouteNote`, `Route Note`, `route-note` và `ROUTE  NOTE` là **một**.
 * Giao diện và API dùng chung hàm này, cùng lý do như `hopDongPhuNgay`: hai
 * luật so trùng khác nhau thì cảnh báo hiện ở màn hình mà API vẫn nhận, hoặc
 * ngược lại.
 */
export function chuanHoaTenDoiTac(ten) {
    return ten
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\u0111/g, 'd')
        .replace(/\u0110/g, 'D')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
}
/** Đối tác đầu tiên trong danh bạ trùng tên với `ten` theo `chuanHoaTenDoiTac`. */
export function timDoiTacTrungTen(danhBa, ten, boQuaId) {
    const khoa = chuanHoaTenDoiTac(ten);
    if (khoa === '')
        return null;
    return danhBa.find((d) => d.id !== boQuaId && chuanHoaTenDoiTac(d.name) === khoa) ?? null;
}
export const setAdsenseLinkInputSchema = z.object({
    status: adsenseLinkStatusSchema,
});
export const listStatementsQuerySchema = cursorQuerySchema.extend({
    partnerId: z.string().optional(),
    period: z.string().optional(),
    status: statementStatusSchema.optional(),
});
/** Bước 1 của wizard import: xem trước kết quả đọc file theo template. */
export const statementPreviewSchema = z.object({
    partnerId: z.string(),
    period: z.string(),
    currency: z.string(),
    rows: z.array(z.object({
        rawChannelRef: z.string(),
        gross: z.string(),
        partnerShare: z.string(),
        net: z.string(),
        /// Kênh khớp tự động theo youtubeChannelId, handle hoặc tên.
        matchedChannelId: z.string().nullable(),
        matchedBy: z.enum(['youtube_id', 'handle', 'name', 'none']),
    })),
    totals: z.object({ gross: z.string(), partnerShare: z.string(), net: z.string() }),
});
export const confirmStatementInputSchema = z.object({
    partnerId: z.string().min(1),
    period: periodMonthSchema,
    currency: z.string().length(3).default('USD'),
    sourceFile: z.string().nullish(),
    lines: z.array(z.object({
        rawChannelRef: z.string(),
        channelId: z.string().nullish(),
        gross: z.number(),
        partnerShare: z.number(),
        net: z.number(),
    })),
});
export const createTicketInputSchema = z.object({
    partnerId: z.string().min(1),
    channelId: z.string().nullish(),
    subject: z.string().trim().min(3).max(200),
    body: z.string().trim().min(3).max(5000),
});
/**
 * Đối tác có đang thực sự trả tiền trong kỳ hay không.
 *
 * Một luật duy nhất cho cả hệ thống: thẻ "Phần chia Network" ở màn Quản lý kênh
 * và dòng "Qua N network đang trả tiền" ở màn Network đều đếm bằng hàm này.
 * Trước bước 12.0 mỗi nơi đếm một kiểu nên ra 6 và 5 (xem mục A3).
 *
 * `adsense_direct` không phải Network nên không tính; đối tác chưa có kênh hoặc
 * chưa có doanh thu trong kỳ ở trạng thái `waiting`, cũng không tính.
 */
export function laDoiTacDangTraTien(doiTac) {
    return doiTac.state === 'paying';
}
/** 4 thẻ chỉ số của màn hình Network. */
export const networksSummarySchema = z.object({
    partnerCount: z.number().int().nonnegative(),
    /// Số **Network** đang thực sự trả tiền trong kỳ sao kê gần nhất.
    payingPartners: z.number().int().nonnegative(),
    channelsViaNetwork: z.number().int().nonnegative(),
    /// Số kênh nhận tiền trực tiếp qua AdSense.
    channelsAdsense: z.number().int().nonnegative(),
    activeContracts: z.number().int().nonnegative(),
    openTickets: z.number().int().nonnegative(),
    latestPeriod: z.string().nullable(),
    latestTotals: statementTotalsSchema.nullable(),
});
/** Kênh nhận AdSense trực tiếp, dùng cho khung cùng tên ở màn hình Doanh thu. */
export const adsenseChannelSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
    partnerId: z.string(),
    partnerName: z.string(),
    linkStatus: adsenseLinkStatusSchema.nullable(),
});
/* ------------------------------------------------------------------ */
/* Template import và tải file (bổ sung ở module 02)                   */
/* ------------------------------------------------------------------ */
/**
 * Giới hạn kích thước file **sau khi giải mã base64**.
 * File gửi lên dạng JSON base64 nên không cần thêm thư viện multipart.
 */
export const GIOI_HAN_FILE_SAO_KE_BYTES = 10 * 1024 * 1024;
export const GIOI_HAN_FILE_HOP_DONG_BYTES = 20 * 1024 * 1024;
/** Các trường mà một dòng sao kê cần có. */
export const TRUONG_SAO_KE = ['channelRef', 'gross', 'partnerShare', 'net'];
export const TRUONG_SAO_KE_LABEL = {
    channelRef: 'Kênh (tên, handle hoặc mã)',
    gross: 'Doanh thu gộp',
    partnerShare: 'Phần Network giữ',
    net: 'Thực nhận',
};
/**
 * Ánh xạ cột file → trường. Mỗi Network có định dạng khác nhau nên lưu riêng.
 * `headerRow` là số thứ tự hàng tiêu đề (1 là hàng đầu tiên).
 */
export const statementMappingSchema = z.object({
    channelRef: z.string().trim().min(1),
    gross: z.string().trim().min(1),
    partnerShare: z.string().trim().min(1).nullish(),
    net: z.string().trim().min(1).nullish(),
    headerRow: z.number().int().min(1).default(1),
    /// Tên sheet của file XLSX; bỏ trống thì lấy sheet đầu tiên.
    sheet: z.string().trim().max(120).nullish(),
    /// Dấu phân cách của file CSV.
    delimiter: z.string().min(1).max(1).default(','),
});
export const statementImportTemplateSchema = z.object({
    id: z.string(),
    partnerId: z.string(),
    name: z.string(),
    mapping: statementMappingSchema,
});
export const statementTemplateInputSchema = z.object({
    name: z.string().trim().min(1).max(120),
    mapping: statementMappingSchema,
});
/** Bước 1 của wizard: gửi file dạng base64 kèm template (nếu đã có). */
export const previewStatementInputSchema = z.object({
    partnerId: z.string().min(1),
    period: periodMonthSchema,
    currency: z.string().length(3).default('USD'),
    fileName: z.string().trim().min(1).max(260),
    /// Nội dung file đã mã hoá base64.
    contentBase64: z.string().min(1),
    templateId: z.string().nullish(),
    /// Ánh xạ cột dùng ngay cho lần này, không cần lưu template.
    mapping: statementMappingSchema.nullish(),
});
/** Tải file hợp đồng lên `storage/networks/`. */
export const uploadFileInputSchema = z.object({
    fileName: z.string().trim().min(1).max(260),
    contentBase64: z.string().min(1),
});
export const uploadFileResultSchema = z.object({
    fileUrl: z.string(),
    fileName: z.string(),
    size: z.number().int().nonnegative(),
});
/** Một dòng của bảng "Kênh theo Network". */
export const partnerChannelRowSchema = z.object({
    linkId: z.string(),
    channelId: z.string(),
    channelName: z.string(),
    channelHandle: z.string(),
    avatarColor: z.string(),
    partnerId: z.string(),
    partnerName: z.string(),
    partnerKind: partnerKindSchema,
    sharePercent: z.string().nullable(),
    splitLabel: z.string().nullable(),
    /**
     * Hợp đồng dòng link này đang gắn. `null` = **chưa gắn hợp đồng** (cũng là
     * trường hợp hợp đồng đã xoá mềm): tỷ lệ Network của link về 0 % và thuế Mỹ
     * rơi về mặc định, nên cột "Hợp đồng" phải hiện chip cảnh báo.
     */
    contractId: z.string().nullable(),
    contractNo: z.string().nullable(),
    contractEndDate: z.iso.datetime().nullable(),
    joinedAt: z.iso.datetime(),
    leftAt: z.iso.datetime().nullable(),
    linkStatus: adsenseLinkStatusSchema.nullable(),
    /// Số liệu kỳ đang xem, lấy từ sao kê của kỳ đó.
    gross: z.string(),
    partnerShare: z.string(),
    net: z.string(),
});
/** Số liệu một thẻ đối tác ở lưới "Đối tác thanh toán". */
export const partnerCardSchema = partnerSchema.extend({
    /**
     * `true` khi số tiền của thẻ là **tạm tính từ YouTube API** (kỳ chưa có sao
     * kê). Giao diện phải ghi rõ, không để người đọc tưởng là số đã đối soát.
     */
    tamTinh: z.boolean(),
    /// Doanh thu gộp của kỳ đang xem, `null` khi chưa có sao kê.
    gross: z.string().nullable(),
    /// `paying` khi có tiền trong kỳ; `no_channel` khi chưa gán kênh.
    state: z.enum(['paying', 'waiting', 'no_channel', 'adsense_pending', 'adsense_linked']),
    /**
     * Số link **đang hoạt động** của đối tác chưa gắn hợp đồng nào. Lớn hơn 0 là
     * số tiền của những kênh đó đang sai: tỷ lệ Network 0 % và thuế Mỹ mặc định.
     */
    soLinkChuaGanHopDong: z.number().int().min(0),
});
export const listPartnersQuerySchema = z.object({
    period: periodMonthSchema.optional(),
    /** **Mã kỳ** của ô chọn kỳ chung — thay dần `period` (xem `@qlk/shared/ky.ts`). */
    ky: maKySchema.optional(),
});
/* ------------------------------------------------------------------ */
/* Tiền của một kỳ (trang Network)                                     */
/* ------------------------------------------------------------------ */
/** Tiền của một kênh / một đối tác trong kỳ, đã đi hết chuỗi `tinhThucNhan`. */
export const tienKySchema = z.object({
    gross: z.string(),
    thueMy: z.string(),
    partnerShare: z.string(),
    vat: z.string(),
    thucNhan: z.string(),
});
/**
 * Tiền của **đúng kỳ đang chọn** cho trang Network: tổng ba ô chỉ số, số trên
 * từng thẻ đối tác và từng dòng kênh.
 *
 * Trước 23/10/2026 trang này không có ô chọn kỳ: ba ô đọc sao kê của **kỳ gần
 * nhất** còn thẻ đối tác đọc một kỳ khác, nên ba con số cạnh nhau nói về ba
 * khoảng thời gian.
 */
export const tienNetworkKySchema = z.object({
    ky: z.string(),
    nhanKy: z.string(),
    from: z.string(),
    to: z.string(),
    /**
     * Kỳ là một **tháng đã chốt** ⇒ số lấy từ ảnh chụp lúc chốt và sao kê, khớp
     * trang Đối soát. `false` ⇒ giao diện gắn chip "tạm tính".
     */
    daChot: z.boolean(),
    tong: tienKySchema,
    theoDoiTac: z.array(tienKySchema.extend({ partnerId: z.string() })),
    theoKenh: z.array(tienKySchema.extend({ channelId: z.string() })),
});
//# sourceMappingURL=networks.js.map